<script lang="ts">
  import ActionsMenu, { type ActionItem } from './ActionsMenu.svelte';
  import ConfirmDialog from './ConfirmDialog.svelte';
  import Icon from './Icon.svelte';
  import { deleteGroup } from './api';
  import { DEFAULT_NEARBY_RADIUS_KM } from '~/shared/group-actions.js';
  import type { GroupView } from '~/shared/api.js';

  let {
    group,
    poiCount,
    tripToken,
    editable,
    onDeleted,
    onEdit,
    onSurprise,
    onClosest,
    onNearby,
    onShowOnMap,
  }: {
    group: GroupView;
    poiCount: number;
    tripToken: string;
    editable: boolean;
    onDeleted: (groupId: number) => void;
    onEdit: (group: GroupView) => void;
    onSurprise: (group: GroupView) => void;
    onClosest: (group: GroupView) => void;
    onNearby: (group: GroupView) => void;
    onShowOnMap: (group: GroupView) => void;
  } = $props();

  let deleting: boolean = $state(false);
  let confirmingDelete: boolean = $state(false);

  const empty = $derived(poiCount === 0);

  function requestDelete() {
    if (!editable || deleting) return;
    confirmingDelete = true;
  }

  async function confirmDelete() {
    confirmingDelete = false;
    deleting = true;
    try {
      await deleteGroup(tripToken, group.id);
      onDeleted(group.id);
    } catch (e) {
      console.error('Failed to delete group:', e);
    } finally {
      deleting = false;
    }
  }

  const menuItems = $derived<ActionItem[]>([
    { kind: 'button', label: 'Edit group', onClick: () => onEdit(group) },
    {
      kind: 'button',
      label: 'Delete group',
      destructive: true,
      disabled: deleting,
      onClick: requestDelete,
    },
  ]);
</script>

{#if confirmingDelete}
  <ConfirmDialog
    title="Delete group?"
    message={`Delete the "${group.name}" group? Places in this group stay in your trip.`}
    confirmLabel="Delete group"
    destructive
    onConfirm={confirmDelete}
    onCancel={() => (confirmingDelete = false)}
  />
{/if}

<section class="card-group">
  <header class="head">
    <div class="title">
      <span class="dot dot--{group.tint}" aria-hidden="true"></span>
      <h3 class="m-0 truncate">{group.name}</h3>
      <span class="count">{poiCount} {poiCount === 1 ? 'place' : 'places'}</span>
    </div>
    {#if editable}
      <ActionsMenu items={menuItems} label="Group options" />
    {/if}
  </header>

  <div class="actions">
    <button
      class="btn btn-pill"
      type="button"
      disabled={empty}
      title={empty ? 'Add a place to this group first' : ''}
      onclick={() => onSurprise(group)}
    >
      <Icon name="shuffle" size={15} strokeWidth={2} />
      <span>Surprise me</span>
    </button>
    <button
      class="btn-icon"
      type="button"
      aria-label="Nearest place to me"
      disabled={empty}
      title={empty ? 'Add a place to this group first' : 'Nearest place from your location'}
      onclick={() => onClosest(group)}
    >
      <Icon name="map-pin" size={17} />
    </button>
    <button
      class="btn-icon"
      type="button"
      aria-label="Show on map"
      disabled={empty}
      title={empty ? 'Add a place to this group first' : 'Show on map'}
      onclick={() => onShowOnMap(group)}
    >
      <Icon name="map" size={17} />
    </button>
    <button
      class="btn-icon"
      type="button"
      aria-label="Nearby in my list"
      disabled={empty}
      title={empty ? 'Add a place to this group first' : `Other trip places within ${DEFAULT_NEARBY_RADIUS_KM} km`}
      onclick={() => onNearby(group)}
    >
      <Icon name="sparkles" size={17} />
    </button>
  </div>
</section>

<style>
  .card-group {
    background: var(--color-canvas);
    border: 1px solid var(--color-hairline-soft);
    border-radius: var(--radius-md);
    padding: 1.25rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
  .head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
  }
  .title {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    min-width: 0;
  }
  .title h3 {
    font-size: 18px;
    font-weight: 600;
    color: var(--color-ink);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .count {
    font-size: 13px;
    color: var(--color-muted);
  }
  .actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
  }
  /* Bigger gap on phones so the filled "Surprise me" pill doesn't
     visually crowd the icon-circle buttons next to it. */
  @media (max-width: 640px) {
    .actions { gap: 0.625rem; }
  }
  .actions button:disabled { opacity: 0.6; cursor: not-allowed; }
</style>
